import React, { useState, useEffect } from 'react';
import { FrownOutlined, MehOutlined, SmileOutlined } from '@ant-design/icons';
import { getUnorderableProducts } from '../services/productApi';

// Icon mapping based on fabric type
const getIconForFabric = (fabricType) => {
  switch(fabricType) {
    case 'پلی استری':
      return <FrownOutlined className="red-text-darken-2" style={{ fontSize: "20px" }} />;
    case 'نخی':
      return <MehOutlined style={{ fontSize: "20px" }} />;
    case 'نخ پنبه اعلا':
      return <SmileOutlined className="green-text-darken-2" style={{ fontSize: "20px" }} />;
    default:
      return <MehOutlined style={{ fontSize: "20px" }} />;
  }
};

// Helper function to create variant structure dynamically
const createVariantStructure = (productData, category, productType, color) => {
  const fabricTypes = ['پلی استری', 'نخی', 'نخ پنبه اعلا'];
  const baseKey = `${category}${productType}${color}`;
  
  const variants = [
    {
      key: `${baseKey}-header`,
      label: (
        <span className="font-4 purple-text">
          نوع جنس
        </span>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    }
  ];

  fabricTypes.forEach((fabric, index) => {
    const productKey = `${category}${productType}-${color}-${index + 1}`;
    const productInfo = productData.find(p => p.slug === productKey);
    const price = productInfo ? productInfo.price : 0;
    const priceText = price ? `~ ${price.toLocaleString()} هزار` : 'قیمت نامشخص';

    variants.push({
      key: productKey,
      label: (
        <div>
          <a className="font-3">
            {fabric}
            <span className="font-2 gray-text-darken-1"> {priceText}</span>
          </a>
          <a href={`/product/${productKey}`} className="font-1 maxContent" style={{display: 'block', fontSize: '12px', marginTop: '4px', width:"maxContent" }}>
            مشاهده محصول اولیه
          </a>
        </div>
      ),
      icon: getIconForFabric(fabric),
    });
  });

  return variants;
};

// Create a global variable to store the variants
let globalVariants = {
  // Initialize with empty arrays
  MenTShirtVariants_White: [],
  MenTShirtVariants_Black: [],
  MenTShirtVariants_Grey: [],
  MenCollarTShirtVariants_White: [],
  MenCollarTShirtVariants_Black: [],
  MenCollarTShirtVariants_Grey: [],
  MenShirtVariants_White: [],
  MenShirtVariants_Black: [],
  MenShirtVariants_Grey: [],
  MenHoodieVariants_White: [],
  MenHoodieVariants_Black: [],
  MenHoodieVariants_Grey: [],
  
  WomenTShirtVariants_White: [],
  WomenTShirtVariants_Black: [],
  WomenTShirtVariants_Grey: [],
  WomenCollarTShirtVariants_White: [],
  WomenCollarTShirtVariants_Black: [],
  WomenCollarTShirtVariants_Grey: [],
  WomenShirtVariants_White: [],
  WomenShirtVariants_Black: [],
  WomenShirtVariants_Grey: [],
  WomenHoodieVariants_White: [],
  WomenHoodieVariants_Black: [],
  WomenHoodieVariants_Grey: [],
  WomenCropTShirtVariants_White: [],
  WomenCropTShirtVariants_Black: [],
  WomenCropTShirtVariants_Grey: [],
  
  BoysTShirtVariants_White: [],
  BoysTShirtVariants_Black: [],
  BoysTShirtVariants_Grey: [],
  BoysCollarTShirtVariants_White: [],
  BoysCollarTShirtVariants_Black: [],
  BoysCollarTShirtVariants_Grey: [],
  BoysShirtVariants_White: [],
  BoysShirtVariants_Black: [],
  BoysShirtVariants_Grey: [],
  BoysHoodieVariants_White: [],
  BoysHoodieVariants_Black: [],
  BoysHoodieVariants_Grey: [],
  
  GirlsTShirtVariants_White: [],
  GirlsTShirtVariants_Black: [],
  GirlsTShirtVariants_Grey: [],
  GirlsCollarTShirtVariants_White: [],
  GirlsCollarTShirtVariants_Black: [],
  GirlsCollarTShirtVariants_Grey: [],
  GirlsShirtVariants_White: [],
  GirlsShirtVariants_Black: [],
  GirlsShirtVariants_Grey: [],
  GirlsHoodieVariants_White: [],
  GirlsHoodieVariants_Black: [],
  GirlsHoodieVariants_Grey: [],
};

// Function to fetch and update variants
const fetchAndUpdateVariants = async () => {
  try {
    const response = await getUnorderableProducts();
    const productData = response.data.ObjList || [];
    
    // Define all product configurations
    const productConfigs = [
      // Men
      { category: 'Men', productType: 'TShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Men', productType: 'CollarTShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Men', productType: 'Shirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Men', productType: 'Hoodie', colors: ['White', 'Black', 'Grey'] },
      
      // Women
      { category: 'Women', productType: 'TShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Women', productType: 'CollarTShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Women', productType: 'Shirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Women', productType: 'Hoodie', colors: ['White', 'Black', 'Grey'] },
      { category: 'Women', productType: 'CropTShirt', colors: ['White', 'Black', 'Grey'] },
      
      // Boys
      { category: 'Boys', productType: 'TShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Boys', productType: 'CollarTShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Boys', productType: 'Shirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Boys', productType: 'Hoodie', colors: ['White', 'Black', 'Grey'] },
      
      // Girls
      { category: 'Girls', productType: 'TShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Girls', productType: 'CollarTShirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Girls', productType: 'Shirt', colors: ['White', 'Black', 'Grey'] },
      { category: 'Girls', productType: 'Hoodie', colors: ['White', 'Black', 'Grey'] },
    ];
    
    productConfigs.forEach(config => {
      config.colors.forEach(color => {
        const variantKey = `${config.category}${config.productType}Variants_${color}`;
        globalVariants[variantKey] = createVariantStructure(
          productData,
          config.category,
          config.productType,
          color
        );
      });
    });
    
    // Trigger a custom event to notify components that variants have been updated
    window.dispatchEvent(new CustomEvent('variantsUpdated', { detail: globalVariants }));
  } catch (error) {
    console.error('Error fetching product variants:', error);
  }
};

// Start fetching immediately
fetchAndUpdateVariants();

// Create proxy objects for each variant that will dynamically return the updated values
const createDynamicProxy = (variantName) => {
  return new Proxy(globalVariants[variantName] || [], {
    get(target, prop) {
      // If the property is a symbol or function, handle it normally
      if (typeof prop === 'symbol' || prop === 'then' || prop === 'map' || prop === 'filter' || prop === 'forEach') {
        const currentValue = globalVariants[variantName] || [];
        const value = currentValue[prop];
        return typeof value === 'function' ? value.bind(currentValue) : value;
      }
      
      // For array indices, get from current global value
      const currentValue = globalVariants[variantName] || [];
      if (prop === 'length') {
        return currentValue.length;
      }
      return currentValue[prop];
    },
    
    set(target, prop, value) {
      // Prevent direct setting
      return false;
    },
    
    ownKeys(target) {
      const currentValue = globalVariants[variantName] || [];
      return Object.keys(currentValue);
    },
    
    getOwnPropertyDescriptor(target, prop) {
      const currentValue = globalVariants[variantName] || [];
      return Object.getOwnPropertyDescriptor(currentValue, prop);
    }
  });
};

// Export the variants as proxies that will always have the latest data
// This maintains backward compatibility with existing imports
export const MenTShirtVariants_White = createDynamicProxy('MenTShirtVariants_White');
export const MenTShirtVariants_Black = createDynamicProxy('MenTShirtVariants_Black');
export const MenTShirtVariants_Grey = createDynamicProxy('MenTShirtVariants_Grey');
export const MenCollarTShirtVariants_White = createDynamicProxy('MenCollarTShirtVariants_White');
export const MenCollarTShirtVariants_Black = createDynamicProxy('MenCollarTShirtVariants_Black');
export const MenCollarTShirtVariants_Grey = createDynamicProxy('MenCollarTShirtVariants_Grey');
export const MenShirtVariants_White = createDynamicProxy('MenShirtVariants_White');
export const MenShirtVariants_Black = createDynamicProxy('MenShirtVariants_Black');
export const MenShirtVariants_Grey = createDynamicProxy('MenShirtVariants_Grey');
export const MenHoodieVariants_White = createDynamicProxy('MenHoodieVariants_White');
export const MenHoodieVariants_Black = createDynamicProxy('MenHoodieVariants_Black');
export const MenHoodieVariants_Grey = createDynamicProxy('MenHoodieVariants_Grey');

export const WomenTShirtVariants_White = createDynamicProxy('WomenTShirtVariants_White');
export const WomenTShirtVariants_Black = createDynamicProxy('WomenTShirtVariants_Black');
export const WomenTShirtVariants_Grey = createDynamicProxy('WomenTShirtVariants_Grey');
export const WomenCollarTShirtVariants_White = createDynamicProxy('WomenCollarTShirtVariants_White');
export const WomenCollarTShirtVariants_Black = createDynamicProxy('WomenCollarTShirtVariants_Black');
export const WomenCollarTShirtVariants_Grey = createDynamicProxy('WomenCollarTShirtVariants_Grey');
export const WomenShirtVariants_White = createDynamicProxy('WomenShirtVariants_White');
export const WomenShirtVariants_Black = createDynamicProxy('WomenShirtVariants_Black');
export const WomenShirtVariants_Grey = createDynamicProxy('WomenShirtVariants_Grey');
export const WomenHoodieVariants_White = createDynamicProxy('WomenHoodieVariants_White');
export const WomenHoodieVariants_Black = createDynamicProxy('WomenHoodieVariants_Black');
export const WomenHoodieVariants_Grey = createDynamicProxy('WomenHoodieVariants_Grey');
export const WomenCropTShirtVariants_White = createDynamicProxy('WomenCropTShirtVariants_White');
export const WomenCropTShirtVariants_Black = createDynamicProxy('WomenCropTShirtVariants_Black');
export const WomenCropTShirtVariants_Grey = createDynamicProxy('WomenCropTShirtVariants_Grey');

export const BoysTShirtVariants_White = createDynamicProxy('BoysTShirtVariants_White');
export const BoysTShirtVariants_Black = createDynamicProxy('BoysTShirtVariants_Black');
export const BoysTShirtVariants_Grey = createDynamicProxy('BoysTShirtVariants_Grey');
export const BoysCollarTShirtVariants_White = createDynamicProxy('BoysCollarTShirtVariants_White');
export const BoysCollarTShirtVariants_Black = createDynamicProxy('BoysCollarTShirtVariants_Black');
export const BoysCollarTShirtVariants_Grey = createDynamicProxy('BoysCollarTShirtVariants_Grey');
export const BoysShirtVariants_White = createDynamicProxy('BoysShirtVariants_White');
export const BoysShirtVariants_Black = createDynamicProxy('BoysShirtVariants_Black');
export const BoysShirtVariants_Grey = createDynamicProxy('BoysShirtVariants_Grey');
export const BoysHoodieVariants_White = createDynamicProxy('BoysHoodieVariants_White');
export const BoysHoodieVariants_Black = createDynamicProxy('BoysHoodieVariants_Black');
export const BoysHoodieVariants_Grey = createDynamicProxy('BoysHoodieVariants_Grey');

export const GirlsTShirtVariants_White = createDynamicProxy('GirlsTShirtVariants_White');
export const GirlsTShirtVariants_Black = createDynamicProxy('GirlsTShirtVariants_Black');
export const GirlsTShirtVariants_Grey = createDynamicProxy('GirlsTShirtVariants_Grey');
export const GirlsCollarTShirtVariants_White = createDynamicProxy('GirlsCollarTShirtVariants_White');
export const GirlsCollarTShirtVariants_Black = createDynamicProxy('GirlsCollarTShirtVariants_Black');
export const GirlsCollarTShirtVariants_Grey = createDynamicProxy('GirlsCollarTShirtVariants_Grey');
export const GirlsShirtVariants_White = createDynamicProxy('GirlsShirtVariants_White');
export const GirlsShirtVariants_Black = createDynamicProxy('GirlsShirtVariants_Black');
export const GirlsShirtVariants_Grey = createDynamicProxy('GirlsShirtVariants_Grey');
export const GirlsHoodieVariants_White = createDynamicProxy('GirlsHoodieVariants_White');
export const GirlsHoodieVariants_Black = createDynamicProxy('GirlsHoodieVariants_Black');
export const GirlsHoodieVariants_Grey = createDynamicProxy('GirlsHoodieVariants_Grey');

// Optional: Export a function to force refresh the variants
export const refreshVariants = fetchAndUpdateVariants;

// Optional: Export a hook for components that need to react to variant updates
export const useVariantsUpdate = (callback) => {
  React.useEffect(() => {
    window.addEventListener('variantsUpdated', callback);
    return () => window.removeEventListener('variantsUpdated', callback);
  }, [callback]);
};